import { Fragment, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { formatTime } from "@/lib/dates";
import {
  BookOpen,
  Check,
  CheckCheck,
  CornerUpLeft,
  Heart,
  Languages,
  Loader2,
  MoreHorizontal,
  Pencil,
  SpellCheck,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EDIT_MESSAGE,
  DELETE_MESSAGE,
  TOGGLE_REACTION,
  TRANSLATE_MESSAGE,
  CREATE_MESSAGE_CORRECTION,
  CONVERSATION_MESSAGES,
} from "./queries";
import { useChat } from "./ChatContext";
import { useAuth } from "@/lib/auth";
import { AttachmentPreview } from "./AttachmentPreview";
import { ReplyQuote } from "./ReplyQuote";
import { VocabularyPopup } from "./VocabularyPopup";
import { wordDiff } from "./wordDiff";
import { captureLanguageOptions } from "./captureLanguages";
import { segmentSavedWords } from "./savedWords";
import { requestKeepSpelling } from "./keepSpelling";
import {
  canKeepSpelling,
  correctedFromText,
  entryDisplayText,
  entryStateKey,
  failureCodeKey,
  invalidateAfterCapture,
  retryErrorKey,
  roleCanKeepSpelling,
} from "@/lib/vocabulary-entries";
import { removeFlashcardCapture, retryFlashcardCapture } from "@/lib/live/flashcard-capture";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { VocabularyEntryStateEnum } from "@/__generated__/graphql";
import type { ChatMessage, ChatCorrection, ChatUser, ChatVocabularyEntry } from "./types";

interface Props {
  message: ChatMessage;
  isOwn: boolean;
  conversationId: string;
  currentUserId: string;
  // Conversation context for vocabulary capture: the tutor (if any) sets the
  // default target language; the support thread never counts as a tutor.
  otherParticipant?: ChatUser | null;
  isSupportThread?: boolean;
  // Conversation.tutorCanCapture: whether a tutor viewer's "Save for
  // ‹student›" would be accepted here.
  tutorCanCapture?: boolean;
  onJumpToMessage?: (messageId: string) => void;
}

export function MessageBubble({
  message,
  isOwn,
  conversationId,
  currentUserId,
  otherParticipant = null,
  isSupportThread = false,
  tutorCanCapture = false,
  onJumpToMessage,
}: Props) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body ?? "");
  const [showTranslation, setShowTranslation] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [vocabOpen, setVocabOpen] = useState(false);
  const [pop, setPop] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctedDraft, setCorrectedDraft] = useState(message.body ?? "");
  const [correctionNote, setCorrectionNote] = useState("");

  const { setReplyTarget } = useChat();
  const { user: viewer } = useAuth();

  const refetch = [
    { query: CONVERSATION_MESSAGES, variables: { conversationId, limit: 50 } },
  ];

  const [editMessage] = useMutation(EDIT_MESSAGE, { refetchQueries: refetch });
  const [deleteMessage] = useMutation(DELETE_MESSAGE, { refetchQueries: refetch });
  const [toggleReaction] = useMutation(TOGGLE_REACTION, { refetchQueries: refetch });
  const [translateMessage, { loading: translating }] = useMutation(TRANSLATE_MESSAGE);
  const [createCorrection, { loading: correcting }] = useMutation(CREATE_MESSAGE_CORRECTION, { refetchQueries: refetch });

  // A tutor can suggest a correction on a student's (non-own) text message.
  const canCorrect = viewer?.role === "tutor" && !isOwn && message.messageType === "text" && !!message.body;
  const corrections = message.corrections ?? [];

  const submitCorrection = async () => {
    if (!correctedDraft.trim()) return;
    const result = await createCorrection({
      variables: { input: { messageId: message.id, correctedBody: correctedDraft.trim(), note: correctionNote.trim() || undefined } },
    });
    const errors = result.data?.createMessageCorrection?.errors ?? [];
    if (errors.length > 0) {
      setTranslationError(errors.join(", "));
      return;
    }
    setCorrectionOpen(false);
    setCorrectionNote("");
  };

  const within = (mins: number) =>
    Date.now() - new Date(message.createdAt).getTime() < mins * 60 * 1000;
  const editable = isOwn && within(15) && !message.deletedAt;
  const deletableForEveryone = isOwn && within(60) && !message.deletedAt;

  const myHeart = (message.reactions ?? []).find(
    (r) => r.user.id === currentUserId && r.reaction === "heart",
  );
  const heartCount = (message.reactions ?? []).filter((r) => r.reaction === "heart").length;

  const onSaveEdit = async () => {
    if (!draft.trim()) return;
    await editMessage({ variables: { input: { id: message.id, body: draft } } });
    setEditing(false);
  };

  const onToggleHeart = () => {
    setPop(true);
    setTimeout(() => setPop(false), 300);
    toggleReaction({ variables: { input: { messageId: message.id, reaction: "heart" } } });
  };

  const onTranslate = async () => {
    if (showTranslation) {
      setShowTranslation(false);
      return;
    }
    if (translation) {
      setShowTranslation(true);
      return;
    }
    setTranslationError(null);
    const result = await translateMessage({
      variables: { input: { messageId: message.id } },
    });
    const errors = result.data?.translateMessage?.errors ?? [];
    if (errors.length > 0) {
      setTranslationError(errors.join(", "));
      return;
    }
    setTranslation(result.data?.translateMessage?.translation?.translatedBody ?? null);
    setShowTranslation(true);
  };

  if (message.deletedForEveryone) {
    return (
      <div
        id={`message-${message.id}`}
        className={cn("flex w-full", isOwn ? "justify-end" : "justify-start")}
      >
        <div className="max-w-[78%] rounded-2xl bg-muted px-4 py-2 text-sm italic text-muted-foreground">
          {t("chat.messageDeleted")}
        </div>
      </div>
    );
  }

  if (message.deletedAt && isOwn) {
    return (
      <div id={`message-${message.id}`} className="flex w-full justify-end">
        <div className="max-w-[78%] rounded-2xl bg-muted px-4 py-2 text-sm italic text-muted-foreground">
          {t("chat.messageDeletedByYou")}
        </div>
      </div>
    );
  }

  const hasReply = !!message.replyToMessage;
  const firstAttachment = message.attachments?.[0];
  const isAttachmentOnly = !message.body && !!firstAttachment;
  const isImageOnly = isAttachmentOnly && firstAttachment?.kind === "image";

  // The text the translate / save-vocab actions operate on: a text message's
  // body, or — for a voice note — its transcript once transcription is ready.
  const voiceTranscript =
    message.messageType === "voice"
      ? message.attachments?.find((a) => a.kind === "voice")?.transcript ?? null
      : null;
  const translatableText =
    message.messageType === "text" ? message.body || null : voiceTranscript;

  // Save-vocabulary needs a language to save into: a student's learning
  // languages (tutor's first), or — for a tutor — their own teaching language
  // when the other participant is a student. See captureLanguageOptions.
  const { options: captureLanguages, defaultCode: defaultCaptureLanguage } = captureLanguageOptions({
    viewerRole: viewer?.role,
    viewerTeachingLanguage: viewer?.tutorProfile?.teachingLanguage ?? null,
    studentLanguages: viewer?.studentProfile?.studentLanguages.map((sl) => sl.language) ?? [],
    otherParticipant,
    otherParticipantRole: otherParticipant?.role,
    isSupportThread,
    tutorCanCapture,
  });
  const canSaveVocabulary = !!translatableText && captureLanguages.length > 0;

  return (
    <div
      id={`message-${message.id}`}
      className={cn(
        "group/msg flex w-full transition-colors",
        isOwn ? "justify-end" : "justify-start",
      )}
    >
      <div
        className={cn(
          "flex max-w-[78%] flex-col gap-1 md:max-w-[68%]",
          isOwn ? "items-end" : "items-start",
        )}
      >
        {/* Bubble */}
        <div
          className={cn(
            // max-w-full: a bubble sized by its widest unbreakable content (a quoted line,
            // a long link) would ignore its wrapper's 78% and run off the phone (#1495).
            "relative max-w-full text-[14px] leading-[21px] shadow-sm",
            isOwn
              ? "bubble-tail-r bg-foreground text-background"
              : "bubble-tail-l bg-secondary text-foreground",
            // Compact padding when reply or attachment is present so the inset elements have their own breathing room
            hasReply || firstAttachment ? "p-1.5" : "px-4 py-2.5",
            isImageOnly && "p-1.5",
          )}
        >
          {hasReply && (
            <ReplyQuote
              isOwn={isOwn}
              target={message.replyToMessage!}
              currentUserId={currentUserId}
              onTap={() =>
                message.replyToMessage && onJumpToMessage?.(message.replyToMessage.id)
              }
            />
          )}

          {firstAttachment && (
            <div className={cn(hasReply && "mt-1.5")}>
              <AttachmentPreview
                attachment={firstAttachment}
                isOwn={isOwn}
                hasCaption={!!message.body}
              />
            </div>
          )}

          {editing ? (
            <div className={cn("flex gap-2", (hasReply || firstAttachment) && "mt-2 px-2")}>
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSaveEdit()}
                className="text-sm"
                autoFocus
              />
              <button
                onClick={onSaveEdit}
                className="rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground focus-ring"
              >
                {t("chat.save")}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="rounded-md px-3 text-xs focus-ring"
              >
                {t("chat.cancel")}
              </button>
            </div>
          ) : (
            message.body && (
              <p
                className={cn(
                  "whitespace-pre-wrap break-words",
                  firstAttachment && "mt-2 px-1.5 pb-0.5",
                  hasReply && !firstAttachment && "px-2 pt-1.5",
                )}
              >
                {message.vocabularyEntries?.length ? (
                  <SavedWordsBody
                    viewerRole={viewer?.role}
                    studentFirstName={viewer?.role === "tutor" ? otherParticipant?.firstName ?? null : null}
                    body={message.body}
                    entries={message.vocabularyEntries}
                  />
                ) : (
                  message.body
                )}
              </p>
            )
          )}

          {/* Translation block */}
          {showTranslation && translation && !editing && (
            <div
              className={cn(
                "mt-2 border-t pt-2 text-[13px] leading-5",
                isOwn ? "border-background/20 text-background/85" : "border-border text-muted-foreground",
                hasReply && !firstAttachment && "mx-2",
                firstAttachment && "px-1.5",
              )}
            >
              <div
                className={cn(
                  "mb-0.5 text-[10px] font-medium uppercase tracking-wide",
                  isOwn ? "text-background/60" : "text-primary",
                )}
              >
                {t("chat.translation")}
              </div>
              {translation}
            </div>
          )}
          {translationError && (
            <div
              className={cn(
                "mt-1 text-[11px] italic text-destructive",
                hasReply && !firstAttachment && "px-2",
                firstAttachment && "px-1.5",
              )}
            >
              {translationError}
            </div>
          )}

          {/* Tutor corrections (inline diff) */}
          {corrections.length > 0 && !editing && (
            <div
              className={cn(
                "mt-2 border-t pt-2 text-[13px] leading-5",
                isOwn ? "border-background/20" : "border-border",
                hasReply && !firstAttachment && "mx-2",
                firstAttachment && "px-1.5",
              )}
            >
              {corrections.map((c) => (
                <CorrectionBlock key={c.id} correction={c} isOwn={isOwn} />
              ))}
            </div>
          )}

          {/* Meta row (time + edited + heart + read receipts) */}
          <div
            className={cn(
              "mt-1 flex items-center gap-1.5 text-[10px]",
              isOwn ? "justify-end text-background/65" : "text-muted-foreground",
              isAttachmentOnly && "px-1.5 pb-0.5",
              hasReply && !firstAttachment && "px-2 pb-0.5",
            )}
          >
            <span>{formatTime(message.createdAt, i18n.language)}</span>
            {message.editedAt && <span className="italic opacity-80">{t("chat.edited")}</span>}
            {heartCount > 0 && (
              <span
                className={cn(
                  "ml-1 inline-flex items-center gap-0.5",
                  pop && "heart-pop",
                )}
              >
                <Heart className="size-3 fill-heart text-heart" />
                {heartCount > 1 && <span className="font-medium">{heartCount}</span>}
              </span>
            )}
            {isOwn &&
              (message.read ? (
                <CheckCheck className="size-3.5 text-primary" />
              ) : (
                <Check className="size-3.5 opacity-70" />
              ))}
          </div>
        </div>

        {/* Action row. Hidden until hover only where hovering exists: Tailwind's
            hover variants apply under `@media (hover: hover)`, so a row that
            starts at opacity-0 and relies on group-hover to appear never
            appears on a phone — and its invisible buttons still take taps. */}
        {!editing && (
          <div
            className={cn(
              "flex items-center gap-0.5 transition-opacity [@media(hover:hover)]:opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100",
              isOwn ? "flex-row-reverse" : "",
            )}
          >
            <ActionBtn label={myHeart ? t("chat.removeHeart") : t("chat.reactWithHeart")} active={!!myHeart} onClick={onToggleHeart}>
              <Heart className={cn("size-3.5", myHeart && "fill-heart text-heart")} />
            </ActionBtn>
            <ActionBtn label={t("chat.reply")} onClick={() => setReplyTarget(message)}>
              <CornerUpLeft className="size-3.5" />
            </ActionBtn>
            {translatableText && (
              <ActionBtn label={showTranslation ? t("chat.hideTranslation") : t("chat.translate")} active={showTranslation} onClick={onTranslate}>
                {translating ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Languages className="size-3.5" />
                )}
              </ActionBtn>
            )}
            {canSaveVocabulary && (
              <ActionBtn
                label={
                  viewer?.role === "tutor"
                    ? t("chat.saveVocabularyFor", { name: otherParticipant?.firstName ?? "" })
                    : t("chat.saveVocabulary")
                }
                onClick={() => setVocabOpen(true)}
              >
                <BookOpen className="size-3.5" />
              </ActionBtn>
            )}
            {canCorrect && (
              <ActionBtn
                label={t("chat.suggestCorrection")}
                onClick={() => {
                  setCorrectedDraft(message.body ?? "");
                  setCorrectionNote("");
                  setCorrectionOpen(true);
                }}
              >
                <SpellCheck className="size-3.5" />
              </ActionBtn>
            )}
            {(editable || isOwn || deletableForEveryone) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label={t("chat.more")}
                    className="inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-ring"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {editable && (
                    <DropdownMenuItem onClick={() => setEditing(true)}>
                      <Pencil className="mr-2 size-4" />
                      {t("chat.edit")}
                    </DropdownMenuItem>
                  )}
                  {isOwn && (
                    <DropdownMenuItem
                      onClick={() =>
                        deleteMessage({ variables: { input: { id: message.id, scope: "me" } } })
                      }
                    >
                      <Trash2 className="mr-2 size-4" />
                      {t("chat.deleteForMe")}
                    </DropdownMenuItem>
                  )}
                  {deletableForEveryone && (
                    <DropdownMenuItem
                      onClick={() =>
                        deleteMessage({
                          variables: { input: { id: message.id, scope: "everyone" } },
                        })
                      }
                    >
                      <Trash2 className="mr-2 size-4" />
                      {t("chat.deleteForEveryone")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>

      {canSaveVocabulary && translatableText && (
        <VocabularyPopup
          messageId={message.id}
          body={translatableText}
          open={vocabOpen}
          onClose={() => setVocabOpen(false)}
          languages={captureLanguages}
          defaultLanguageCode={defaultCaptureLanguage}
          viewerRole={viewer?.role}
          studentFirstName={viewer?.role === "tutor" ? otherParticipant?.firstName ?? null : null}
        />
      )}

      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("chat.suggestCorrectionTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-muted/50 p-2 text-sm text-muted-foreground">{message.body}</div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">{t("chat.correctedVersion")}</label>
              <Textarea value={correctedDraft} onChange={(e) => setCorrectedDraft(e.target.value)} rows={3} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">{t("chat.note")} <span className="text-muted-foreground">{t("chat.optional")}</span></label>
              <Input value={correctionNote} onChange={(e) => setCorrectionNote(e.target.value)} placeholder={t("chat.correctionNotePlaceholder")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCorrectionOpen(false)}>{t("chat.cancel")}</Button>
            <Button
              onClick={submitCorrection}
              disabled={correcting || !correctedDraft.trim() || correctedDraft.trim() === (message.body ?? "").trim()}
            >
              {t("chat.sendCorrection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorrectionBlock({ correction, isOwn }: { correction: ChatCorrection; isOwn: boolean }) {
  const { t } = useTranslation();
  const segs = wordDiff(correction.originalBody, correction.correctedBody);
  return (
    <div className="mb-1.5 last:mb-0">
      <div
        className={cn(
          "mb-0.5 text-[10px] font-medium uppercase tracking-wide",
          isOwn ? "text-background/60" : "text-primary",
        )}
      >
        {t("chat.correction")}
      </div>
      <p className="whitespace-pre-wrap break-words">
        {segs.map((s, i) =>
          s.type === "del" ? (
            <span key={i} className="text-destructive line-through">{s.value}</span>
          ) : s.type === "ins" ? (
            <span key={i} className="font-medium text-success">{s.value}</span>
          ) : (
            <span key={i}>{s.value}</span>
          ),
        )}
      </p>
      {correction.note && (
        <p className={cn("mt-0.5 text-[11px] italic", isOwn ? "text-background/70" : "text-muted-foreground")}>
          {correction.note}
        </p>
      )}
    </div>
  );
}

// The words a flashcard was captured from, underlined in the state's colour
// (`.saved-word[data-entry-state]` in index.css — the same rules as the
// classroom capture mark) with the state in the hover title. Entries come
// batched per conversation on Message.vocabularyEntries and are refreshed by
// the vocabularyEntryUpdated subscription in ConversationView.
function SavedWordsBody({
  body,
  entries,
  viewerRole,
  studentFirstName = null,
}: {
  body: string;
  entries: ChatVocabularyEntry[];
  viewerRole?: string;
  studentFirstName?: string | null;
}) {
  return (
    <>
      {segmentSavedWords(body, entries).map((segment, i) =>
        segment.entry ? (
          // Keyed by the ENTRY, never by the position: segmentSavedWords claims
          // one span per entry, so the id is unique among these siblings, and it
          // is the only identity that survives the list changing. Undoing an
          // earlier word, saving another, or an edit to the body shifts every
          // later segment down one — and a positional key then hands the next
          // word the previous one's component, with its in-flight flag, its open
          // popover and its ticking clock. The flag is cleared only by a refusal,
          // and no request was ever made for that word, so it would say
          // "Restoring your spelling…" for as long as the bubble is on screen.
          <SavedWord
            key={segment.entry.id}
            text={segment.text}
            entry={segment.entry}
            viewerRole={viewerRole}
            studentFirstName={studentFirstName}
          />
        ) : (
          <Fragment key={i}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

// Re-renders every second while `active`, so a button gated on the server's
// 60-second buffer leaves the screen the moment the server would refuse it.
function useTickingClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return active ? now : Date.now();
}

// The queued toast's Undo lives 8 seconds; the spec's 60-second undo (any
// time for the student), and Retry on a failed capture, need a home in the
// chat after it is gone. The underlined word opens a small card with the
// state, the failure reason, and the same actions the classroom strip has —
// against the same server rules (owner any time, tutor inside the buffer).
function SavedWord({
  text,
  entry,
  viewerRole,
  studentFirstName = null,
}: {
  text: string;
  entry: ChatVocabularyEntry;
  viewerRole?: string;
  studentFirstName?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // This client's revert is in flight: the action stays mounted and says so.
  const [restoring, setRestoring] = useState(false);
  const stateLabel = t("chat.savedWord.tooltip", { state: t(entryStateKey(entry.outcome)) });
  const failed = entry.state === VocabularyEntryStateEnum.Failed;
  // The chat bubble is permanent, unlike the classroom strip: a card that has
  // been CREATED is removed from Browse (with its review history in view),
  // never by a click on a word in an old message. Here Undo is the 60-second
  // undo: the student while the capture is still in flight, the tutor —
  // whoever captured — while it is still queued inside the buffer: the
  // server's can_remove? and the classroom strip's canUndoCapture, which this
  // used to be stricter than (it hid Undo on the student's capture).
  const inFlight = entry.state === VocabularyEntryStateEnum.Pending || entry.state === VocabularyEntryStateEnum.Processing;
  const now = useTickingClock(open && inFlight);
  const canUndo =
    inFlight &&
    (viewerRole === "student" ||
      (viewerRole === "tutor" &&
        entry.state === VocabularyEntryStateEnum.Pending &&
        !!entry.scheduledAt &&
        Date.parse(entry.scheduledAt) > now));

  // What the card is about: the corrected spelling when there is one, and the
  // captured word beside it — never swapped in silently. Every action on the
  // card names this, so the card's heading and its buttons agree.
  const displayText = entryDisplayText(entry.sourceText, entry.correctedText);
  const correctedFrom = correctedFromText(entry.sourceText, entry.correctedText);

  const undo = () => {
    setOpen(false);
    removeFlashcardCapture(entry.id)
      .then((result) => {
        if (result.success) {
          toast.info(t("chat.vocabularyPopup.undoneToast"));
          invalidateAfterCapture(qc);
        } else {
          toast.error(t("liveSession.vocabularyStrip.undoFailedToast"));
        }
      })
      .catch(() => toast.error(t("liveSession.vocabularyStrip.undoFailedToast")));
  };

  const retry = () => {
    setOpen(false);
    retryFlashcardCapture(entry.id)
      .then((result) => {
        if (result.errorCode || !result.vocabularyEntry) {
          toast.error(t(retryErrorKey(result.errorCode)));
          return;
        }
        toast.success(t("liveSession.vocabularyStrip.retryQueuedToast", { word: displayText }));
        invalidateAfterCapture(qc);
      })
      .catch(() => toast.error(t("liveSession.vocabularyStrip.retryFailedToast")));
  };

  // canKeepSpelling is the shared definition of "there is a correction nobody
  // has claimed yet"; roleCanKeepSpelling is the shared bound on who may take
  // it — the student always, a tutor only while the capture has not been
  // generated, because past that the rebuild destroys her cards and the server
  // answers a tutor not_found. It also refuses an unknown role, so a viewer
  // whose role has not loaded is never handed her first-person wording. The
  // clause beside them is this surface's: never on a failed capture, because
  // the correction is written before the picture and audio legs, so a media
  // failure leaves a row still carrying one with no card to rebuild — the
  // server answers that with not_correctable, and Retry is what such a row
  // wants. A chat bubble is permanent, which is why this card in particular
  // must not go on offering a tutor something the server refuses for ever.
  // `|| restoring` keeps the button on screen through THIS card's own restore,
  // exactly as the strip keeps its own: the refetch that lands mid-restore
  // carries skipCorrection, and gating on that alone takes the control out from
  // under the person who just pressed it. `restoring` is local to this card, so
  // every OTHER surface — the tutor's screen, this student's other tab — still
  // stands down on the claim, which is the half that must not offer a second
  // tap. The two surfaces answer one capture the same way.
  const canKeep =
    !failed && roleCanKeepSpelling(viewerRole, entry.state) && (canKeepSpelling(entry) || restoring);
  const keepSpelling = () => {
    // Deliberately local to this card: a revert taken from the conversation's
    // toast leaves this card still offering the action, because nothing tells
    // one saved word what another surface asked. Clicking it then reads
    // correctly — the server answers in_progress and that sentence is shown.
    // Hoisting the flag would mean a second store of the state the server
    // already owns, and it is what keeps the two halves right: the card that
    // asked holds its button, every other one stands down on skipCorrection.
    setRestoring(true);
    // The card stays open and the button stays put for the whole restore — the
    // round trip and the rebuild after it. Only a refusal hands it back; the
    // claim landing does not, because unmounting the control a click is still
    // sitting on is what this branch ruled out on the strip.
    void requestKeepSpelling(entry.id, { t, qc }).then((accepted) => {
      if (!accepted) setRestoring(false);
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="saved-word focus-ring rounded-sm"
          data-entry-state={entry.state}
          data-entry-id={entry.id}
          title={stateLabel}
          // Read out by screen readers through the ::after rule in index.css
          // (generated content is announced but never selected or copied, so
          // the message body stays copy-clean).
          data-state-label={stateLabel}
        >
          {text}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-2 p-3 text-sm">
        {/* The card carries the corrected spelling; the message keeps what was
            typed, which is why the underline still matches sourceText. */}
        <p className="font-medium text-foreground">{displayText}</p>
        {correctedFrom && (
          <p data-testid="saved-word-corrected-from" className="text-xs text-muted-foreground">
            {t("chat.vocabularyPopup.correctedFrom", { original: correctedFrom })}
          </p>
        )}
        <p className="text-muted-foreground">{stateLabel}</p>
        {failed && <p className="text-xs text-destructive">{t(failureCodeKey(entry.failureCode))}</p>}
        {(canUndo || failed || canKeep) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {canUndo && (
              <Button size="sm" variant="outline" onClick={undo}>
                {t("liveSession.vocabularyStrip.undoWord", { word: displayText })}
              </Button>
            )}
            {failed && (
              <Button size="sm" onClick={retry}>
                {t("liveSession.vocabularyStrip.retryWord", { word: displayText })}
              </Button>
            )}
            {canKeep && (
              <Button
                size="sm"
                variant="outline"
                data-testid="saved-word-keep-spelling"
                // The SAME element through the revert, disabled in place
                // rather than unmounted or `disabled`: the click leaves focus
                // here, and the accessible name turning into the restoring
                // line is what announces the new state.
                aria-disabled={restoring || undefined}
                className={cn(restoring && "cursor-default")}
                onClick={() => {
                  if (!restoring) keepSpelling();
                }}
              >
                {/* The in-flight line replaces a role-aware label, so it is
                    role-aware too: "your spelling" is the student's, and a
                    tutor is watching the student's go back. */}
                {restoring
                  ? t(
                      viewerRole === "tutor"
                        ? "chat.vocabularyPopup.restoringToastTutor"
                        : "chat.vocabularyPopup.restoringToast",
                    )
                  : viewerRole === "tutor"
                    ? // A conversation can have no other participant (a deleted
                      // account, a thread still loading), and an empty name
                      // renders "Keep 's spelling" in every catalog. The file's
                      // own translated fallback, the one the header and the
                      // report dialog already use.
                      t("chat.vocabularyPopup.keepSpellingTutor", { name: studentFirstName || t("chat.defaultUser") })
                    : t("chat.vocabularyPopup.keepSpelling")}
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ActionBtn({
  children,
  label,
  active,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-ring",
        active && "text-primary",
      )}
    >
      {children}
    </button>
  );
}
